import type { Practice, Segment, Settings, Timeline } from '../types';
import { buildTimeline, clamp, MAX_POSE_SECONDS, MAX_REPEAT, MIN_POSE_SECONDS, poseTiming, segmentAt } from './timeline';

/** Everything the engine needs from the outside world. Real one: webAudioBackend. Tests: a fake. */
export interface AudioBackend {
  /** Seconds on a monotonic clock that keeps running while the screen is locked. */
  now(): number;
  /** Called synchronously inside the Begin tap so iOS lets audio play. */
  unlock(): void;
  /** Ring a bell at clock time `at`. Returns a function that cancels it. */
  scheduleBell(at: number, volume: number): () => void;
  startMusic(opts: { src: string; loop: boolean; volume: number; fadeInSec: number; seekTo?: number }): void;
  pauseMusic(): void;
  resumeMusic(): void;
  seekMusic(seconds: number): void;
  setMusicVolume(volume: number): void;
  fadeOutMusic(seconds: number): void;
  stopMusic(): void;
  /** Keeps the audio session alive (silent loop) when there is no music. */
  keepAlive(on: boolean): void;
  /** Called when the music element stops by itself (track finished). */
  onMusicEnded(cb: () => void): void;
}

export type Status = 'countdown' | 'running' | 'paused' | 'complete';

export interface Snapshot {
  status: Status;
  /** Seconds from timeline start; negative during the countdown. */
  position: number;
  total: number;
  countdown: number; // 3,2,1 or 0
  segment: Segment;
  segmentIndex: number;
  segmentElapsed: number;
  segmentRemaining: number;
  segmentCount: number;
  round: number;
  rounds: number;
  poseNumber: number; // 1-based within the round
  poseCount: number;
  /** Bumps every time a bell is due, so the UI can pulse once. */
  bellCount: number;
  elapsedForHistory: number;
}

const COUNTDOWN_SECONDS = 3;
const LOOKAHEAD_SECONDS = 90;
const FADE_OUT_SECONDS = 5;
const TICK_MS = 250;

export interface EngineOptions {
  practice: Practice;
  settings: Settings;
  backend: AudioBackend;
  /** Called when overrides change so the UI layer can persist them. */
  onSettingsChange?: (settings: Settings) => void;
  onComplete?: (info: { seconds: number }) => void;
  onSegmentChange?: (segment: Segment) => void;
  /** Chained sessions keep the audio session alive between practices. */
  holdAudio?: boolean;
}

export class SessionEngine {
  readonly practice: Practice;
  private settings: Settings;
  private timeline: Timeline;
  private backend: AudioBackend;
  private status: Status = 'countdown';
  private anchor = 0; // clock time that equals position 0
  private pausedAt = 0; // position when paused
  private scheduled = new Map<number, () => void>(); // bell position -> cancel
  private bellsRung = 0;
  private lastSegment = -1;
  private timer: ReturnType<typeof setInterval> | null = null;
  private listeners = new Set<() => void>();
  private snapshot!: Snapshot;
  private opts: EngineOptions;
  private completed = false;
  private finishedElapsed = 0;

  constructor(opts: EngineOptions) {
    this.opts = opts;
    this.practice = opts.practice;
    this.settings = opts.settings;
    this.backend = opts.backend;
    this.timeline = buildTimeline(opts.practice, opts.settings);
    this.refreshSnapshot();
  }

  // ---- lifecycle ----

  /** Call inside the Begin tap. */
  begin() {
    this.backend.unlock();
    this.anchor = this.backend.now() + COUNTDOWN_SECONDS;
    this.status = 'countdown';
    this.startAudio();
    this.backend.keepAlive(this.settings.music === 'none' && !this.isTrackPractice());
    this.backend.onMusicEnded(() => this.onMusicEnded());
    this.timer = setInterval(() => this.tick(), TICK_MS);
    this.tick();
  }

  destroy() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.cancelBells();
    this.backend.stopMusic();
    if (!this.opts.holdAudio) this.backend.keepAlive(false);
    this.listeners.clear();
  }

  // ---- state access ----

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = () => this.snapshot;
  getSettings = () => this.settings;
  getTimeline = () => this.timeline;

  private position(): number {
    if (this.status === 'paused') return this.pausedAt;
    if (this.status === 'complete') return this.timeline.total;
    return this.backend.now() - this.anchor;
  }

  private isTrackPractice() {
    return this.practice.steps.some(s => s.kind === 'track');
  }

  // ---- controls ----

  pause() {
    if (this.status === 'paused' || this.status === 'complete') return;
    this.pausedAt = this.position();
    this.status = 'paused';
    this.cancelBells();
    this.backend.pauseMusic();
    this.emit();
  }

  resume() {
    if (this.status !== 'paused') return;
    this.anchor = this.backend.now() - this.pausedAt;
    this.status = this.pausedAt < 0 ? 'countdown' : 'running';
    this.backend.resumeMusic();
    this.tick();
  }

  toggle() {
    if (this.status === 'paused') this.resume();
    else this.pause();
  }

  /** Jump to a position (clamped to the session). */
  seek(position: number) {
    const target = clamp(position, 0, Math.max(0, this.timeline.total - 0.01));
    this.cancelBells();
    if (this.status === 'paused') this.pausedAt = target;
    else {
      this.anchor = this.backend.now() - target;
      if (this.status === 'countdown') this.status = 'running';
    }
    if (this.isTrackPractice()) this.backend.seekMusic(target % Math.max(1, this.trackLength()));
    this.tick();
  }

  jumpToSegment(index: number) {
    const seg = this.timeline.segments[clamp(index, 0, this.timeline.segments.length - 1)];
    this.seek(seg.start);
  }

  next() {
    const cur = this.timeline.segments[segmentAt(this.timeline, this.position())];
    if (cur.index >= this.timeline.segments.length - 1) {
      this.complete();
      return;
    }
    this.jumpToSegment(cur.index + 1);
  }

  previous() {
    const pos = this.position();
    const cur = this.timeline.segments[segmentAt(this.timeline, pos)];
    if (pos - cur.start > 3 || cur.index === 0) this.jumpToSegment(cur.index);
    else this.jumpToSegment(cur.index - 1);
  }

  setMusicVolume(v: number) {
    this.settings = { ...this.settings, musicVolume: clamp(v, 0, 1) };
    this.backend.setMusicVolume(this.settings.musicVolume);
    this.opts.onSettingsChange?.(this.settings);
    this.emit();
  }

  setBellVolume(v: number) {
    this.settings = { ...this.settings, bellVolume: clamp(v, 0, 1) };
    this.cancelBells();
    this.opts.onSettingsChange?.(this.settings);
    this.tick();
  }

  /** Change this pose's length by `delta` seconds, remembered for next time. */
  adjustPoseSeconds(delta: number) {
    const seg = this.currentSegment();
    if (seg.step.kind !== 'pose') return;
    const { seconds } = poseTiming(seg.step, seg.stepIndex, this.settings);
    const next = clamp(seconds + delta, MIN_POSE_SECONDS, MAX_POSE_SECONDS);
    this.applySettings({
      ...this.settings,
      overrides: { ...this.settings.overrides, [seg.stepIndex]: { ...this.settings.overrides[seg.stepIndex], duration: next } },
    });
  }

  /** Change how many times this pose repeats, remembered for next time. */
  adjustPoseRepeat(delta: number) {
    const seg = this.currentSegment();
    if (seg.step.kind !== 'pose') return;
    const { repeat } = poseTiming(seg.step, seg.stepIndex, this.settings);
    const next = clamp(repeat + delta, 1, MAX_REPEAT);
    this.applySettings({
      ...this.settings,
      overrides: { ...this.settings.overrides, [seg.stepIndex]: { ...this.settings.overrides[seg.stepIndex], repeat: next } },
    });
  }

  end() {
    this.finishedElapsed = Math.max(0, this.position());
    this.status = 'complete';
    this.completed = false;
    this.cancelBells();
    this.backend.fadeOutMusic(1);
    if (!this.opts.holdAudio) this.backend.keepAlive(false);
    this.emit();
  }

  // ---- internals ----

  private currentSegment(): Segment {
    return this.timeline.segments[segmentAt(this.timeline, Math.max(0, this.position()))];
  }

  /** Rebuild the timeline and keep the listener at the same spot inside the same pose. */
  private applySettings(next: Settings) {
    const pos = Math.max(0, this.position());
    const cur = this.timeline.segments[segmentAt(this.timeline, pos)];
    const offset = pos - cur.start;
    this.settings = next;
    this.timeline = buildTimeline(this.practice, next);
    const match =
      this.timeline.segments.find(
        s => s.round === cur.round && s.stepIndex === cur.stepIndex && s.repeatIndex === cur.repeatIndex,
      ) ?? this.timeline.segments[Math.min(cur.index, this.timeline.segments.length - 1)];
    const target = match.start + Math.min(offset, Math.max(0, match.seconds - 0.01));
    this.cancelBells();
    if (this.status === 'paused') this.pausedAt = target;
    else this.anchor = this.backend.now() - target;
    this.opts.onSettingsChange?.(next);
    this.tick();
  }

  private startAudio() {
    const s = this.settings;
    const track = this.practice.steps.find(x => x.kind === 'track');
    if (track && track.kind === 'track') {
      this.backend.startMusic({ src: track.src, loop: track.loops > 1, volume: s.musicVolume, fadeInSec: 0.5 });
    } else if (s.music !== 'none') {
      this.backend.startMusic({ src: `/audio/${s.music}.m4a`, loop: true, volume: s.musicVolume, fadeInSec: COUNTDOWN_SECONDS });
    }
  }

  private trackLength(): number {
    const track = this.practice.steps.find(x => x.kind === 'track');
    return track && track.kind === 'track' ? track.seconds : 0;
  }

  private onMusicEnded() {
    // A single-play track ending means the practice is over, even if the clock is a hair early.
    if (this.isTrackPractice() && this.status !== 'complete') {
      const track = this.practice.steps.find(x => x.kind === 'track');
      if (track && track.kind === 'track' && track.loops <= 1) this.complete();
    }
  }

  private cancelBells() {
    this.scheduled.forEach(cancel => cancel());
    this.scheduled.clear();
  }

  private scheduleBells(pos: number) {
    if (this.status === 'paused' || this.status === 'complete') return;
    for (const at of this.timeline.bells) {
      if (this.scheduled.has(at)) continue;
      if (at < pos - 0.05 || at > pos + LOOKAHEAD_SECONDS) continue;
      const clockTime = this.anchor + at;
      this.scheduled.set(at, this.backend.scheduleBell(Math.max(clockTime, this.backend.now()), this.settings.bellVolume));
    }
    // Forget bells that already passed.
    this.scheduled.forEach((_, at) => {
      if (at < pos - 1) this.scheduled.delete(at);
    });
  }

  private complete() {
    if (this.status === 'complete') return;
    this.finishedElapsed = this.timeline.total;
    this.status = 'complete';
    this.completed = true;
    this.backend.fadeOutMusic(FADE_OUT_SECONDS);
    if (!this.opts.holdAudio) this.backend.keepAlive(false);
    this.opts.onComplete?.({ seconds: this.timeline.total });
    this.emit();
  }

  private tick() {
    if (this.status !== 'complete') {
      const pos = this.position();
      if (this.status === 'countdown' && pos >= 0) this.status = 'running';
      if (this.status !== 'paused' && pos >= this.timeline.total) {
        // Bells for the end are already scheduled; let the final one sound before the screen changes.
        this.complete();
        return;
      }
      this.scheduleBells(pos);
      const idx = segmentAt(this.timeline, Math.max(0, pos));
      if (pos >= 0 && idx !== this.lastSegment) {
        this.lastSegment = idx;
        this.opts.onSegmentChange?.(this.timeline.segments[idx]);
      }
      // Count bells whose time has passed so the UI can pulse once.
      const due = this.timeline.bells.filter(b => b <= pos + 0.001).length;
      if (this.status !== 'paused') this.bellsRung = Math.max(this.bellsRung, due);
    }
    this.emit();
  }

  private emit() {
    this.refreshSnapshot();
    this.listeners.forEach(fn => fn());
  }

  private refreshSnapshot() {
    const raw = this.position();
    const pos = clamp(raw, -COUNTDOWN_SECONDS, this.timeline.total);
    const idx = segmentAt(this.timeline, Math.max(0, pos));
    const segment = this.timeline.segments[idx];
    const elapsed = clamp(pos - segment.start, 0, segment.seconds);
    const poseSteps = this.practice.steps.filter(s => s.kind === 'pose').length;
    const stepsBefore = this.practice.steps.slice(0, segment.stepIndex).filter(s => s.kind === 'pose').length;
    this.snapshot = {
      status: this.status,
      position: pos,
      total: this.timeline.total,
      countdown: this.status === 'countdown' ? Math.ceil(-pos) : 0,
      segment,
      segmentIndex: idx,
      segmentElapsed: elapsed,
      segmentRemaining: Math.max(0, segment.seconds - elapsed),
      segmentCount: this.timeline.segments.length,
      round: segment.round,
      rounds: this.practice.free ? 1 : this.practice.roundsAdjustable ? this.settings.rounds : this.practice.rounds,
      poseNumber: poseSteps ? stepsBefore + 1 : 1,
      poseCount: poseSteps || 1,
      bellCount: this.bellsRung,
      elapsedForHistory: this.status === 'complete' ? this.finishedElapsed : Math.max(0, pos),
    };
  }

  get wasCompleted() {
    return this.completed;
  }
}
